import {
  RequestCookie,
  ResponseCookie,
} from 'next/dist/compiled/@edge-runtime/cookies';
import type { cookies } from 'next/headers';

/**
 * Set or remove a session cookie
 */
export function setCookie(
  name: string,
  value: string | undefined | null,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  // Set the cookie domain to the top level one, so they work across subdomains as well.
  const domain = window.location.hostname.replace(
    /^.*?\.((singsaver|moneyhero|moneymax|money101)\..+)$/,
    '$1',
  );
  let cookie = `${name}=${value ?? ''}; Path=/; Domain=${domain}`;
  if (value == null) cookie += '; Max-Age=0';
  document.cookie = cookie;
}

/**
 * Returns cookies as an object
 */
export function getCookies(): Record<string, string> {
  return Object.fromEntries(parseCookies());
}

/**
 * Returns the client equivalent of next.js cookies()
 */
export function clientCookies(): CookieJar {
  return new ClientCookies();
}

export type CookieJar = Awaited<ReturnType<typeof cookies>>;

function parseCookies(): Array<[string, string]> {
  if (typeof window === 'undefined') {
    return [];
  }
  return document.cookie.split(/; */g).map((cookie) => {
    const idx = cookie.indexOf('=');
    return [cookie.slice(0, idx), cookie.slice(idx + 1)];
  });
}

class ClientCookies implements CookieJar {
  #cookies: RequestCookie[];
  size: number;

  constructor() {
    this.#cookies = parseCookies().map((cookie) => {
      return { name: cookie[0], value: cookie[1] };
    });
    this.size = this.#cookies.length;
  }

  get(name: string): RequestCookie | undefined {
    return this.#cookies.find((it) => it.name === name);
  }

  getAll(name: string): RequestCookie[] {
    return this.#cookies.filter((it) => it.name === name);
  }

  has(name: string): boolean {
    return this.#cookies.find((it) => it.name === name) != null;
  }

  set(
    ...args:
      | [key: string, value: string, cookie?: Partial<ResponseCookie>]
      | [options: ResponseCookie]
  ): this {
    if (typeof args[0] === 'string') {
      setCookie(args[0], args[1]);
    } else {
      setCookie(args[0].name, args[0].value);
    }
    return this;
  }

  delete(
    ...args:
      | [key: string]
      | [options: Omit<ResponseCookie, 'value' | 'expires'>]
  ): this {
    if (typeof args[0] === 'string') {
      setCookie(args[0], undefined);
    } else {
      setCookie(args[0].name, undefined);
    }
    return this;
  }

  [Symbol.iterator](): IterableIterator<[string, RequestCookie]> {
    return this.#cookies
      .map((it) => [it.name, it] as [string, RequestCookie])
      [Symbol.iterator]();
  }
}
